import React, { useEffect, useRef } from "react";
import { Spin, Alert } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import AuthModal from "./components/AuthModal";
import QueueManagementPanel from "./components/QueueManagementPanel";
import BusinessTypeList from "./components/BusinessTypeList";
import QueueList from "./components/QueueList";
import UpdateProfileModal from "./components/UpdateProfileModal";
import LocationModal from "./components/LocationModal";
import AlertModal from "./components/AlertModal";
import VerificationModal from "./components/VerificationModal";
import SearchComponent from "./components/SearchComponent";
import PasswordResetModal from "./components/PasswordResetModal";
import LocationRequiredModal from "./components/LocationRequiredModal";
import DetailsModal from "./components/DetailsModal";
import ContactUsModal from "./components/ContactUsModal";
import FaqModal from "./components/FaqModal";
import { useAppLogic } from "./hooks/useAppLogic";
import localforage from "localforage";
import "./App.css";

function App({ API_URL }) {
  const logicState = useAppLogic(API_URL);
  
  // Destructure whatever we need for UI rendering
  const {
    currentLocation, setCurrentLocation, tempLocation, setTempLocation,
    isLoggedIn, currentRadius, setCurrentRadius,
    showLocationModal, setShowLocationModal,
    showChangeLocationModal, setShowChangeLocationModal,
    showSidebar, setShowSidebar,
    showAuthModal, setShowAuthModal,
    authMode, setAuthMode,
    showAlertModal, setShowAlertModal,
    showUserStatus, currentNumber, queueStatus,
    showVerificationModal, setShowVerificationModal,
    otpSent, setOtpSent, otpResendTimer, otpTimerCount,
    otp, setOtp, otpVerified, showResetPasswordModal, setShowResetPasswordModal,
    loading, selectedBusinessType, setSelectedBusinessType,
    businessName, isMobile, queueData,
    showCustomBusinessType, setShowCustomBusinessType,
    customBusinessType, setCustomBusinessType,
    showUpdateProfileModal, setShowUpdateProfileModal,
    typeOfBusinesses, userAddress, buttonLoading,
    detailsModal, setDetailsModal, businessDetails,
    businessMessage, contactUsVisible, setContactUsVisible,
    faqVisible, setFaqVisible, searchValue, setSearchValue,
    profileForm, form1, form2,

    requestLocation, fetchNearbyBusinesses, getFavorites, handleSearch,
    updateProfile, signup, login, logOut, setBusinessMessageForUsers,
    handleSendOtp, handleVerifyOtp, handleResetPassword,
    handleIncrement, handleDecrement, handleReset, toggleActiveStatus, setNumber,
    openDetailsModal, refreshBusiness, deleteAccount, contactUsSubmit
  } = logicState;

  const buttonRef = useRef(null);

  // Draggable button effect
  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    let isDragging = false;
    let offsetX, offsetY;

    const onMouseDown = (e) => {
      isDragging = true;
      offsetX = e.clientX - button.getBoundingClientRect().left;
      offsetY = e.clientY - button.getBoundingClientRect().top;
    };

    const onMouseMove = (e) => {
      if (isDragging) {
        button.style.left = `${e.clientX - offsetX}px`;
        button.style.top = `${e.clientY - offsetY}px`;
      }
    };

    const onMouseUp = () => {
      isDragging = false;
    };

    const onTouchStart = (e) => {
      const touch = e.touches[0];
      isDragging = true;
      offsetX = touch.clientX - button.getBoundingClientRect().left;
      offsetY = touch.clientY - button.getBoundingClientRect().top;
    };

    const onTouchMove = (e) => {
      const touch = e.touches[0];
      if (isDragging) {
        button.style.left = `${touch.clientX - offsetX}px`;
        button.style.top = `${touch.clientY - offsetY}px`;
      }
    };

    const onTouchEnd = () => {
      isDragging = false;
    };

    button.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    button.addEventListener("touchstart", onTouchStart, { passive: false });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);

    return () => {
      button.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      button.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  return (
    <div className="app-container">
      <div className="HeaderContent">
        <Header
          selectedBusinessType={selectedBusinessType}
          isLoggedIn={isLoggedIn}
          currentLocation={currentLocation}
          setShowSidebar={setShowSidebar}
          setSelectedBusinessType={setSelectedBusinessType}
          fetchNearbyBusinesses={fetchNearbyBusinesses}
          showUserStatus={showUserStatus}
          setShowChangeLocationModal={setShowChangeLocationModal}
          isMobile={isMobile}
          getFavorites={getFavorites}
          setSearchValue={setSearchValue}
          businessName={businessName}
        />
        <Sidebar
          isLoggedIn={isLoggedIn}
          setShowAuthModal={setShowAuthModal}
          setShowSidebar={setShowSidebar}
          setShowUpdateProfileModal={setShowUpdateProfileModal}
          logOut={logOut}
          showSidebar={showSidebar}
          setContactUsVisible={setContactUsVisible}
          setFaqVisible={setFaqVisible}
        />
        {selectedBusinessType && !isLoggedIn && (
          <SearchComponent
            handleSearch={handleSearch}
            currentLocation={currentLocation}
            selectedBusinessType={selectedBusinessType}
            fetchNearbyBusinesses={fetchNearbyBusinesses}
            getFavorites={getFavorites}
            loading={loading}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        )}
      </div>
      <LocationRequiredModal
        showLocationModal={showLocationModal}
        setShowLocationModal={setShowLocationModal}
      />
      <AuthModal
        form2={form2}
        authMode={authMode}
        setAuthMode={setAuthMode}
        login={login}
        signup={signup}
        handleSendOtp={handleSendOtp}
        handleVerifyOtp={handleVerifyOtp}
        otpSent={otpSent}
        otpResendTimer={otpResendTimer}
        otpTimerCount={otpTimerCount}
        setOtp={setOtp}
        buttonLoading={buttonLoading}
        typeOfBusinesses={typeOfBusinesses}
        showCustomBusinessType={showCustomBusinessType}
        setShowCustomBusinessType={setShowCustomBusinessType}
        customBusinessType={customBusinessType}
        setCustomBusinessType={setCustomBusinessType}
        otpVerified={otpVerified}
        otp={otp}
        showAuthModal={showAuthModal}
        setShowAuthModal={setShowAuthModal}
        setOtpSent={setOtpSent}
      />
      <LocationModal
        showChangeLocationModal={showChangeLocationModal}
        setShowChangeLocationModal={setShowChangeLocationModal}
        form1={form1}
        currentLocation={currentLocation}
        currentRadius={currentRadius}
        requestLocation={requestLocation}
        fetchNearbyBusinesses={fetchNearbyBusinesses}
        setCurrentRadius={setCurrentRadius}
        tempLocation={tempLocation}
        setCurrentLocation={setCurrentLocation}
        setSearchValue={setSearchValue}
      />
      <VerificationModal
        showVerificationModal={showVerificationModal}
        setShowVerificationModal={setShowVerificationModal}
      />
      <AlertModal
        showAlertModal={showAlertModal}
        setShowAlertModal={setShowAlertModal}
      />
      <DetailsModal
        setDetailsModal={setDetailsModal}
        detailsModal={detailsModal}
        businessDetails={businessDetails}
      />
      <ContactUsModal
        contactUsSubmit={contactUsSubmit}
        contactUsVisible={contactUsVisible}
        setContactUsVisible={setContactUsVisible}
      />
      <FaqModal
        faqVisible={faqVisible}
        setFaqVisible={setFaqVisible}
        setContactUsVisible={setContactUsVisible}
      />
      <UpdateProfileModal
        showUpdateProfileModal={showUpdateProfileModal}
        setShowUpdateProfileModal={setShowUpdateProfileModal}
        updateProfile={updateProfile}
        profileForm={profileForm}
        businessName={businessName}
        userAddress={userAddress}
        requestLocation={requestLocation}
        buttonLoading={buttonLoading}
        deleteAccount={deleteAccount}
      />
      <PasswordResetModal
        showResetPasswordModal={showResetPasswordModal}
        setShowResetPasswordModal={setShowResetPasswordModal}
        handleResetPassword={handleResetPassword}
        buttonLoading={buttonLoading}
      />

      <div
        ref={buttonRef}
        role="button"
        style={{
          display:
            selectedBusinessType && isMobile && !isLoggedIn ? "flex" : "none",
          position: "fixed",
          bottom: "70px",
          right: "50px",
          zIndex: 1000,
          cursor: "pointer",
          backgroundColor: "#1890ff",
          borderRadius: "50%",
          width: "60px",
          height: "60px",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          transition: "background-color 0.3s ease, transform 0.3s ease",
          touchAction: "none",
        }}
        onClick={async () => {
          let favoritesEnable = await localforage.getItem("favorites_enable");
          favoritesEnable
            ? getFavorites()
            : fetchNearbyBusinesses(currentLocation);
          setSearchValue("")
        }}
      >
        <ReloadOutlined style={{ fontSize: "30px", color: "#fff" }} />
      </div>
      <main
        className="main-content"
        style={{
          marginTop: selectedBusinessType ? "100px" : "60px",
          height: "calc(100vh - 68px)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {loading ? (
          <Spin style={{ height: "500px" }} tip="Loading...">
            <Alert message="Content is loading..." type="info" />
          </Spin>
        ) : !isLoggedIn ? (
          selectedBusinessType ? (
            <QueueList
              queueData={queueData}
              selectedBusinessType={selectedBusinessType}
              isMobile={isMobile}
              setShowAlertModal={setShowAlertModal}
              openDetailsModal={openDetailsModal}
              buttonLoading={buttonLoading}
              refreshBusiness={refreshBusiness}
            />
          ) : (
            <BusinessTypeList
              typeOfBusinesses={typeOfBusinesses}
              setSelectedBusinessType={setSelectedBusinessType}
              fetchNearbyBusinesses={fetchNearbyBusinesses}
              currentLocation={currentLocation}
            />
          )
        ) : (
          <QueueManagementPanel
            businessName={businessName}
            currentNumber={currentNumber}
            queueStatus={queueStatus}
            handleIncrement={handleIncrement}
            handleDecrement={handleDecrement}
            handleReset={handleReset}
            toggleActiveStatus={toggleActiveStatus}
            businessMessage={businessMessage}
            setBusinessMessageForUsers={setBusinessMessageForUsers}
            setNumber={setNumber}
          />
        )}
      </main>
    </div>
  );
}

export default App;
